import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// --- Child Components & Styles ---
import AddProductModal from "../components/AddProductModal";
import EditCostModal from "../components/EditCostModal";
import "../Styles/ManageProducts.css";
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

const ManageProducts = () => {
  // --- State ---
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Edit Cost modal
  const [isEditCostModalOpen, setIsEditCostModalOpen] = useState(false);
  const [productToEditCost, setProductToEditCost] = useState(null);

  // Search & Pagination State
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 50;

  // --- Hooks ---
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const debounceTimeout = useRef(null);

  // --- Data Fetching ---
  const fetchProducts = useCallback(
    async (query = "", page = 1) => {
      if (!token) {
        navigate("/login");
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/products/search?query=${query}&page=${page}&limit=${itemsPerPage}`,
          { headers }
        );
        setProducts(res.data.items);
        setTotalCount(res.data.total_count);
        setTotalPages(Math.ceil(res.data.total_count / itemsPerPage));
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error("Session expired. Please log in again.");
          navigate("/login");
        } else {
          toast.error("Failed to load products");
        }
      } finally {
        setLoading(false);
      }
    },
    [token, navigate, headers]
  );

  useEffect(() => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchProducts(searchTerm, currentPage);
    }, 300);
  }, [fetchProducts, searchTerm, currentPage]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleOpenEditCostModal = (product) => {
    setProductToEditCost(product);
    setIsEditCostModalOpen(true);
  };

  return (
    <>
      <div className="manage-container">
        {/* --- Top Toolbar with Search + Pagination + Add --- */}
        <div className="toolbar">
          {/* Left: Search */}
          <input
            type="text"
            className="search-input"
            placeholder="Search by product name..."
            value={searchTerm}
            onChange={handleSearchChange}
          />

          {/* Center: Pagination */}
          {!loading && totalCount > 0 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage <= 1}
              >
                ‹
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage >= totalPages}
              >
                ›
              </button>
            </div>
          )}

          {/* Right: Add Product */}
          <button
            className="btn-primary"
            onClick={() => setIsAddModalOpen(true)}
          >
            + Add New Product
          </button>
        </div>

        {/* --- Table --- */}
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>UOM</th>
                <th>Cost Price</th>
                <th>HSN</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="loading-state">
                    Loading...
                  </td>
                </tr>
              ) : products.length > 0 ? (
                products.map((p) => (
                  <tr key={p.prodcode}>
                    <td data-label="Code">
                      <span className="cell-value">{p.prodcode}</span>
                    </td>
                    <td data-label="Name">
                      <span className="cell-value">{p.prodname}</span>
                    </td>
                    <td data-label="UOM">
                      <span className="cell-value">{p.uom}</span>
                    </td>
                    <td data-label="Cost Price">
                      <span className="cell-value">
                        ₹{(p.cost_price || 0).toFixed(2)}
                      </span>
                    </td>
                    <td data-label="HSN">
                      <span className="cell-value">{p.hsncode}</span>
                    </td>

                    {/* Actions */}
                    <td className="actions-cell" data-label="Actions">
                      <button
                        className="btn btn-primary"
                        onClick={() =>
                          navigate(`/masters/products/edit/${p.prodcode}`)
                        }
                      >
                        Edit Details
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleOpenEditCostModal(p)}
                      >
                        Edit Cost
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="empty-state">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Modals --- */}
      <AddProductModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setIsAddModalOpen(false);
          fetchProducts(searchTerm, currentPage);
        }}
      />
      <EditCostModal
        isOpen={isEditCostModalOpen}
        product={productToEditCost}
        onClose={() => setIsEditCostModalOpen(false)}
        onSuccess={() => {
          setIsEditCostModalOpen(false);
          fetchProducts(searchTerm, currentPage);
        }}
      />
    </>
  );
};

export default ManageProducts;
